/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Plus, Trash2, Edit2, Wallet, Copy, ChevronDown, ChevronRight, ExternalLink, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const empty = {
  name: '', environment: 'demo', client_id: '', client_secret: '',
  merchant_id: '', api_key: '', source_code: '',
};

function CopyLine({ value, testId }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success('Copié'); } catch { toast.error('Impossible de copier'); }
  };
  return (
    <div className="flex items-center justify-between gap-2 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
      <code className="font-mono text-xs text-zinc-700 truncate" data-testid={testId}>{value}</code>
      <button type="button" onClick={copy} className="text-xs text-zinc-500 hover:text-zinc-700 shrink-0 flex items-center gap-1">
        <Copy className="h-3 w-3" /> Copier
      </button>
    </div>
  );
}

function Tutorial() {
  const [open, setOpen] = useState(true);
  return (
    <Card className="card-flat overflow-hidden mb-6" data-testid="viva-tutorial">
      <button type="button" onClick={() => setOpen(!open)} className="w-full p-5 flex items-center justify-between hover:bg-zinc-50 transition">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-blue-600 text-white flex items-center justify-center"><Wallet className="h-5 w-5" /></div>
          <div className="text-left">
            <div className="font-display font-bold tracking-tight">Comment brancher un compte Viva Wallet</div>
            <div className="text-xs text-zinc-500">Tuto complet — Smart Checkout (Native) + webhook</div>
          </div>
        </div>
        {open ? <ChevronDown className="h-5 w-5 text-zinc-400" /> : <ChevronRight className="h-5 w-5 text-zinc-400" />}
      </button>
      {open && (
        <div className="px-6 pb-6 pt-1 border-t border-zinc-100 space-y-5 text-sm text-zinc-700">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Viva Wallet opère principalement en <strong>Europe (EUR)</strong>. Le règlement en <strong>USD</strong> n'est pas garanti — confirme avec Viva pour un compte US.</span>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">Étape 1 — Crée / connecte-toi à ton compte Viva</div>
            <p className="text-xs text-zinc-600">
              Va sur <a href="https://www.viva.com" target="_blank" rel="noreferrer" className="text-blue-600 underline inline-flex items-center gap-0.5">viva.com <ExternalLink className="h-3 w-3" /></a> (ou
              <a href="https://demo.vivapayments.com" target="_blank" rel="noreferrer" className="text-blue-600 underline mx-1 inline-flex items-center gap-0.5">demo.vivapayments.com <ExternalLink className="h-3 w-3" /></a> pour tester).
              Choisis l'environnement <strong>Demo</strong> pour les tests, <strong>Production</strong> pour le live.
            </p>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">Étape 2 — Récupère tes identifiants Smart Checkout (Client ID / Secret)</div>
            <ul className="text-xs text-zinc-600 space-y-1 list-disc ml-4">
              <li>Dans le dashboard Viva : <strong>Settings → API Access → Smart Checkout Credentials</strong>.</li>
              <li>Copie le <strong>Client ID</strong> et génère/copie le <strong>Client Secret</strong>.</li>
              <li>Colle-les ci-dessous (le secret est stocké chiffré, jamais réaffiché).</li>
            </ul>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">Étape 3 — Crée un "Payment Source" (Source Code)</div>
            <ul className="text-xs text-zinc-600 space-y-1 list-disc ml-4">
              <li><strong>Sales → Payment Sources → Add</strong> (type <em>Website/App</em>).</li>
              <li>Renseigne le <strong>domaine</strong> autorisé : <code className="font-mono">checkout-commande.live</code> (et tes domaines de checkout). C'est obligatoire pour le paiement carte intégré.</li>
              <li>Récupère le <strong>Source Code</strong> (un nombre, ex. <code className="font-mono">1234</code>) → colle-le ci-dessous.</li>
            </ul>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">Étape 4 — Merchant ID + API Key (pour les webhooks)</div>
            <ul className="text-xs text-zinc-600 space-y-1 list-disc ml-4">
              <li><strong>Settings → API Access</strong> → copie le <strong>Merchant ID</strong> (GUID) et l'<strong>API Key</strong>.</li>
              <li>Ils servent à valider l'URL de webhook (clé de vérification).</li>
            </ul>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">Étape 5 — Enregistre le webhook (finalisation des paiements)</div>
            <p className="text-xs text-zinc-600 mb-2">
              Après avoir créé le compte ci-dessous, son <strong>URL de webhook dédiée</strong> apparaît sur sa carte. Dans Viva :
              <strong> Settings → Webhooks → Create Webhook</strong>, colle l'URL, puis active les événements
              <code className="font-mono mx-1">Transaction Payment Created (1796)</code> et <code className="font-mono">Transaction Failed (1798)</code>.
            </p>
            <p className="text-[11px] text-zinc-500">Viva vérifiera l'URL via un GET (notre serveur renvoie automatiquement la clé). Pas besoin de coller de secret.</p>
          </div>

          <div>
            <div className="font-semibold text-zinc-900 mb-1">Étape 6 — Lie le compte à une vitrine</div>
            <p className="text-xs text-zinc-600">
              Va dans <strong>Sites Vitrines</strong>, choisis le PSP <strong>Viva Wallet</strong> et sélectionne ce compte. Les boutiques de cette vitrine encaisseront alors via Viva.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 flex gap-2">
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Résumé des champs requis : <strong>Client ID</strong>, <strong>Client Secret</strong>, <strong>Source Code</strong> (obligatoires) + <strong>Merchant ID</strong> & <strong>API Key</strong> (pour le webhook).</span>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function VivaAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get('/viva-accounts');
    setAccounts(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name, environment: a.environment || 'demo', client_id: a.client_id || '',
      client_secret: '', merchant_id: a.merchant_id || '', api_key: '', source_code: a.source_code || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error('Nom requis'); return; }
    if (!form.client_id || !form.source_code) { toast.error('Client ID et Source Code requis'); return; }
    if (!editing && !form.client_secret) { toast.error('Client Secret requis à la création'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/viva-accounts/${editing.id}`, form);
        toast.success('Compte Viva mis à jour');
      } else {
        await api.post('/viva-accounts', form);
        toast.success('Compte Viva créé');
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce compte Viva ? (impossible si des vitrines sont liées)')) return;
    try {
      await api.delete(`/viva-accounts/${id}`);
      toast.success('Compte supprimé');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline mb-2">— Infrastructure</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Viva Wallet</h1>
          <p className="text-zinc-600 mt-2">Gérez vos comptes Viva Wallet (PSP alternatif à Stripe). Chaque compte peut être lié à une vitrine.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="admin-add-viva-button" className="btn-primary rounded-md">
              <Plus className="h-4 w-4 mr-2" /> Ajouter un compte Viva
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? 'Modifier le compte Viva' : 'Nouveau compte Viva Wallet'}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                Identifiants Smart Checkout / Native. Les secrets sont stockés chiffrés.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">NOM INTERNE</Label>
                <Input data-testid="admin-viva-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-10 rounded-md" placeholder="Viva · Marque A" />
              </div>
              <div>
                <Label className="text-xs">ENVIRONNEMENT</Label>
                <select data-testid="admin-viva-env-select" value={form.environment}
                  onChange={(e) => setForm({ ...form, environment: e.target.value })}
                  className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white">
                  <option value="demo">Demo (test)</option>
                  <option value="prod">Production (live)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">CLIENT ID (Smart Checkout)</Label>
                <Input data-testid="admin-viva-client-id-input" value={form.client_id}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono text-xs" placeholder="Smart Checkout Client ID" />
              </div>
              <div>
                <Label className="text-xs">CLIENT SECRET {editing && <span className="text-zinc-400 normal-case">(vide = inchangé)</span>}</Label>
                <Input data-testid="admin-viva-client-secret-input" type="password" value={form.client_secret}
                  onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono text-xs"
                  placeholder={editing ? '•••••••• (conservé)' : 'Smart Checkout Client Secret'} />
                <p className="text-xs text-zinc-500 mt-1">Stocké chiffré (Fernet). Jamais retourné par l'API.</p>
              </div>
              <div>
                <Label className="text-xs">SOURCE CODE</Label>
                <Input data-testid="admin-viva-source-input" value={form.source_code}
                  onChange={(e) => setForm({ ...form, source_code: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono text-xs" placeholder="ex: 1234" />
              </div>
              <div>
                <Label className="text-xs">MERCHANT ID (webhooks)</Label>
                <Input data-testid="admin-viva-merchant-input" value={form.merchant_id}
                  onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono text-xs" placeholder="Merchant ID (GUID)" />
              </div>
              <div>
                <Label className="text-xs">API KEY (webhooks) {editing && <span className="text-zinc-400 normal-case">(vide = inchangé)</span>}</Label>
                <Input data-testid="admin-viva-apikey-input" type="password" value={form.api_key}
                  onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono text-xs"
                  placeholder={editing ? '•••••••• (conservé)' : 'API Key'} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} disabled={saving} data-testid="admin-save-viva-button" className="btn-primary rounded-md">
                {editing ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tutorial />

      <div className="grid md:grid-cols-2 gap-4">
        {accounts.length === 0 ? (
          <Card className="card-flat p-10 col-span-full text-center">
            <Wallet className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
            <h3 className="font-display font-bold mb-1">Aucun compte Viva Wallet</h3>
            <p className="text-zinc-500 text-sm">Ajoutez votre premier compte Viva en suivant le tuto ci-dessus.</p>
          </Card>
        ) : accounts.map((a) => (
          <Card key={a.id} className="card-flat p-6" data-testid={`viva-account-card-${a.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-bold tracking-tight mb-1 truncate">{a.name}</div>
                <div className="text-xs text-zinc-500 font-mono">ID {a.client_id || '—'} · secret {a.client_secret_masked || '—'} · source {a.source_code || '—'}</div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-md border font-mono ${a.environment === 'prod' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                    {a.environment === 'prod' ? 'LIVE' : 'DEMO'}
                  </span>
                  <span className={`px-2 py-0.5 rounded-md border font-mono ${a.configured ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    {a.configured ? 'configuré' : 'incomplet'}
                  </span>
                  <span className="px-2 py-0.5 rounded-md border bg-zinc-50 border-zinc-200 font-mono">
                    {a.vitrines_count || 0} vitrine{a.vitrines_count > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="mt-3">
                  <div className="overline mb-1.5">— URL Webhook Viva (à enregistrer dans Viva)</div>
                  <CopyLine value={`${BACKEND_URL}/api/public/viva/webhook/${a.id}`} testId={`viva-webhook-url-${a.id}`} />
                </div>
              </div>
              <div className="flex gap-1 ml-2">
                <Button variant="ghost" size="icon" onClick={() => openEdit(a)} className="rounded-md" data-testid={`admin-edit-viva-${a.id}`}>
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(a.id)} data-testid={`admin-delete-viva-${a.id}`} className="rounded-md text-red-600 hover:text-red-700">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
